import React from "react";
import { Input } from "../../../common/Input";
import { Select } from "../../../common/Select";
import { AreaOfOperationPickerField } from "../../../common/selectComponents/AreaOfOperationPicker";
import { SubAreaPickerField } from "../../../common/selectComponents/SubAreaPicker";

/**
 * Tab 2 — Vessel Summary
 *
 * Left column:
 *   - Vessel Status      : dropdown (VesselStatus) — REQUIRED
 *   - Area of Operation  : AreaOfOperationPickerField (MODIFIED)
 *   - Sub-Area           : SubAreaPickerField (MODIFIED)
 *
 * Right column:
 *   - Vessel Type        : dropdown (VesselType) — REQUIRED
 *   - Vessel Class       : text (disabled — auto-populated)
 *   - Operational Group  : text (disabled — auto-populated)
 */
export const VesselSummaryTab = ({ form, set, options, isViewOnly, errors = {} }) => { // MODIFIED: added errors prop with default value
  const editable = !isViewOnly;

  const handleVesselTypeChange = (e) => {
    const selectedId = e.target.value;
    const match = options.vesselTypes.find(
      (v) => String(v.value) === String(selectedId)
    );
    set("vesselTypeId", selectedId);
    set("vesselClass", match?.vesselClass || "");
    set("operationGroup", match?.operationGroup || "");
  };

  // ADDED: handler for Area of Operation selection
  const handleAreaOfOperationSelect = (area) => {
    set("areaOfOperationId", area.id);
    set("areaOfOperationCode", area.code);
    set("areaOfOperationDescription", area.description);
  };

  // ADDED: handler for Area of Operation clear
  const handleAreaOfOperationClear = () => {
    set("areaOfOperationId", "");
    set("areaOfOperationCode", "");
    set("areaOfOperationDescription", "");
  };

  // ADDED: handler for Sub-Area selection
  const handleSubAreaSelect = (subArea) => {
    set("subAreaId", subArea.id);
    set("subAreaCode", subArea.code);
    set("subAreaDescription", subArea.description);
  };

  // ADDED: handler for Sub-Area clear
  const handleSubAreaClear = () => {
    set("subAreaId", "");
    set("subAreaCode", "");
    set("subAreaDescription", "");
  };

  return (
    <div className="space-y-6 pt-2">
      <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
        <SectionBadge label="Vessel Summary" editable={editable} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-4">
          {/* Left column */}
          <div className="space-y-4">
            <Select
              label="Vessel Status:"
              options={options.vesselStatuses}
              value={form.vesselStatusId || ""}
              onChange={(e) => set("vesselStatusId", e.target.value)}
              disabled={!editable}
              isRequired={true} // ADDED: mark as required
              error={!!errors.vesselStatusId} // ADDED: error prop
              errorMessage={errors.vesselStatusId} // ADDED: errorMessage prop
            />
            
            {/* REPLACED: Select with AreaOfOperationPickerField */}
            <AreaOfOperationPickerField
              label="Area of Operation"
              areaOfOperationId={form.areaOfOperationId || ""}
              displayValue={form.areaOfOperationDescription || ""}
              onSelect={handleAreaOfOperationSelect}
              onClear={handleAreaOfOperationClear}
              disabled={!editable}
              allowClear={false}
              isRequired={true} // ADDED: mark as required
              error={!!errors.areaOfOperationId} // ADDED: error prop
              errorMessage={errors.areaOfOperationId} // ADDED: errorMessage prop
            />

            {/* REPLACED: Select with SubAreaPickerField */}
            <SubAreaPickerField
              label="Sub-Area"
              subAreaId={form.subAreaId || ""}
              displayValue={form.subAreaDescription || ""}
              onSelect={handleSubAreaSelect}
              onClear={handleSubAreaClear}
              disabled={!editable}
              allowClear={true}
              error={!!errors.subAreaId}
              errorMessage={errors.subAreaId}
            />
          </div>

          {/* Right column */}
          <div className="space-y-4">
            <Select
              label="Vessel Type:"
              options={options.vesselTypes}
              value={form.vesselTypeId || ""}
              onChange={handleVesselTypeChange}
              disabled={!editable}
              isRequired={true} // ADDED: mark as required
              error={!!errors.vesselTypeId} // ADDED: error prop
              errorMessage={errors.vesselTypeId} // ADDED: errorMessage prop
            />
            <Input
              label="Vessel Class:"
              value={form.vesselClass || ""}
              disabled={true}
              placeholder="-- Auto-populated --"
            />
            <Input
              label="Operational Group:"
              value={form.operationGroup || ""}
              disabled={true}
              placeholder="-- Auto-populated --"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const SectionBadge = ({ label, editable }) => (
  <div className="flex items-center justify-between mb-4">
    <span className="text-xs font-bold uppercase tracking-widest text-gray-400">
      {label}
    </span>
    <span
      className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${
        editable
          ? "bg-green-50 text-green-700 border-green-200"
          : "bg-gray-100 text-gray-400 border-gray-200"
      }`}
    >
      {editable ? "Editable" : "View Only"}
    </span>
  </div>
);